import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Typography.module.css';

/** UPPERCASE, widely tracked blue label above a title — the books' signature eyebrow. */
export function Eyebrow({ children, className, tone = 'primary' }: { children: ReactNode; className?: string; tone?: 'primary' | 'muted' }) {
  return <div className={cx(s.eyebrow, tone === 'muted' && s.eyebrowMuted, className)}>{children}</div>;
}

type TitleLevel = 1 | 2 | 3 | 4;
const TITLE_TAG: Record<TitleLevel, ElementType> = { 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4' };

/** Headings in Space Grotesk: 1 = page (24px), 2 = section (20px), 3 = card (16px), 4 = caps label. */
export function Title({
  level = 1,
  as,
  children,
  className,
  id,
}: {
  level?: TitleLevel;
  as?: ElementType;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  const Tag = as ?? TITLE_TAG[level];
  return (
    <Tag id={id} className={cx(s.title, s[`title${level}`], className)}>
      {children}
    </Tag>
  );
}

type TextSize = 'xs' | 'sm' | 'md' | 'lg';
type TextTone = 'default' | 'strong' | 'secondary' | 'muted' | 'danger' | 'success' | 'primary';

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  size?: TextSize;
  tone?: TextTone;
  weight?: 'regular' | 'medium' | 'semibold';
  mono?: boolean;
  numeric?: boolean;
  truncate?: boolean;
  measure?: boolean;
}

/** Body text in Inter. `mono` for ids, IMEIs and plates; `numeric` for tabular figures. */
export function Text({ as: Tag = 'p', size = 'md', tone = 'default', weight = 'regular', mono, numeric, truncate, measure, className, ...rest }: TextProps) {
  return (
    <Tag
      className={cx(
        s.text,
        s[`size-${size}`],
        s[`tone-${tone}`],
        s[`weight-${weight}`],
        mono && s.mono,
        numeric && s.numeric,
        truncate && s.truncate,
        measure && s.measure,
        className,
      )}
      {...rest}
    />
  );
}
